// Where the write queue lives between a tab closing and the next one opening.
//
// ── IndexedDB, and its relationship with Firestore's ───────────────────────
//
// **A database of our own, sharing nothing** (#126), although Firestore's own
// persistence uses IndexedDB too. Firestore's is named
// `firestore/[DEFAULT]/<project>/main` and its schema is the SDK's private
// business; ours is `coda-write-queue` with one object store. Two IndexedDB
// databases on one origin are as independent as two files in a directory; the
// only shared resource is the origin's storage quota, which both draw on and
// neither reserves.
//
// What is *not* independent is the data — the same write is in both, once as
// our record and once as the SDK's mutation — and that is #199's subject rather
// than this file's.
//
// ── Why IndexedDB here when #167 uses localStorage next door ───────────────
//
// `outstandingWrites.js` uses `localStorage` for a tiny note that must be
// durable *by the time the next statement runs*: `setItem` is synchronous, so a
// tab dying between the note and the SDK call still leaves the note behind.
// That does not carry over here, for two reasons.
//
// **The payload does not fit.** A queue record holds the document's fields, not
// a path and a noun. `localStorage` is ~5 MB for the whole origin, and this is
// the one structure that grows while offline with no bound — `outstandingWrites.js`
// caps itself at 500 records and lets its count go approximate past the cap,
// which a queue cannot.
//
// **There is no race to lose.** #167 needs durability *before* the SDK call
// because that call is what it races. Here nothing is handed to anybody until
// the record is durable, because the caller's own promise does not resolve
// until it is.
//
// ── The memory store is the test seam ─────────────────────────────────────
//
// jsdom implements no IndexedDB, so the `ui` project drives the queue's rules
// through the memory store with no browser and no network;
// `test/ui/idbStoreArms.test.jsx` runs both arms under `fake-indexeddb`.
//
// It is *also* what a private window gets, which is a degradation: a queue in
// memory does not survive a reload, so a write made offline in a private window
// and then reloaded is lost. `outstandingWrites.js` has the same limit.
//
// `store.describe()` tells the two apart: it returns `memory` for the seam and
// `memory-degraded`, with a reason, for the browser.
//
// ── The storage engine is `idbStore.js`'s (#207's U0, and #208) ───────────
//
// The IndexedDB half lives in `idbStore.js`, shared with the app-state store,
// so #208's fix — probe by *use* and degrade once, loudly — is written once
// rather than twice. The `tx.oncomplete` contract that makes a resolved
// promise mean "on disk" is argued there.

import { createLocalDatabase } from './idbStore';

const DB_NAME = 'coda-write-queue';
const DB_VERSION = 1;
// Keyed by batch id because the batch is the unit of everything here: the core
// dispatches, settles and dead-letters per batch, never per op, since "a batch
// commits or refuses as one".
const STORES = [{ name: 'batches', keyPath: 'batchId' }];
const STORE = STORES[0].name;

const LOST = 'writes queued while offline are held in memory only, so a reload will forget them';

// The queue's five calls, over whichever database `idbStore.js` gave us. The
// engine decides durability; this decides what a queue record is.
function queueStoreOver(database) {
  return {
    /**
     * Which store is actually in use, resolved rather than guessed.
     *
     * Asynchronous because the answer is only known once the store has been
     * probed, and probing is opening a database.
     */
    describe() {
      return database.describe();
    },
    async put(record) {
      await database.apply([{ store: STORE, put: record }]);
      return record;
    },
    async patch(batchId, changes) {
      return database.patch(STORE, batchId, changes);
    },
    async remove(batchId) {
      await database.apply([{ store: STORE, remove: batchId }]);
    },
    async all() {
      // Sorted by `seq`, not by key: the batch id is random and the order the
      // queue replays in is the order the writes were made.
      return (await database.all(STORE)).sort(bySeq);
    },
    async clear() {
      await database.apply([{ store: STORE, clear: true }]);
    },
  };
}

function bySeq(a, b) {
  return a.seq - b.seq;
}

/**
 * A queue store backed by nothing but a Map.
 *
 * Durable for the life of the page and no longer. This is the seam the `ui`
 * project drives the queue's rules through; a browser that cannot give us
 * IndexedDB gets the same behaviour by a different route, and `describe()`
 * tells the two apart.
 */
export function memoryQueueStore() {
  return queueStoreOver(createLocalDatabase({
    name: DB_NAME, version: DB_VERSION, stores: STORES, engine: 'memory', lost: LOST,
  }));
}

/** A queue store backed by IndexedDB. The real one. */
export function indexedDbQueueStore() {
  return queueStoreOver(createLocalDatabase({
    name: DB_NAME, version: DB_VERSION, stores: STORES, engine: 'indexeddb', lost: LOST,
  }));
}

/**
 * The store this environment can actually give us.
 *
 * **Probed by use, not by presence (#208).** A browser that exposes
 * `indexedDB` and refuses to open a database degrades to memory on the first
 * call and says so once, rather than getting a store whose every call rejects.
 * The probe and its limits are argued in `idbStore.js`.
 */
export function defaultQueueStore() {
  return queueStoreOver(createLocalDatabase({
    name: DB_NAME, version: DB_VERSION, stores: STORES, engine: 'auto', lost: LOST,
  }));
}
